import React from "react";
import { Button, Dropdown, Tooltip } from "antd";
import {
  FullscreenExitOutlined,
  FullscreenOutlined,
  MoreOutlined,
  RedoOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { DataGrid } from "react-data-grid";
import Dropdowns from "./Dropdowns.jsx";
import { Footer, LoadingIcon, Sidebar } from "../../../components/index.js";

export function HarvestEntryLayout({
  contextHolder,
  onKeyDown,
  sidebarOpen,
  setSidebarOpen,
  gridWrapRef,
  isPseudoFullscreen,
  isFullscreen,
  FULLSCREEN_Z,
  GRID_MIN_HEIGHT,
  gridHeight,
  borderGray,
  selectionColor,
  selectionFill,
  dropdownProps,
  headerProps,
  gridProps,
}) {
  const {
    undo,
    redo,
    undoStack,
    redoStack,
    bulkActionsOpen,
    setBulkActionsOpen,
    getGridPopupContainer,
    DROPDOWN_Z,
    selectedRowIds,
    loading,
    dropdownLoading,
    bulkMenuItems,
    handleSaveVisible,
    savingAll,
    hasAnyPendingEdits,
    toggleFullscreen,
  } = headerProps;

  const {
    gridRef,
    gridColumns,
    visibleRows,
    onRowsChange,
    onFill,
    onCellClick,
    onSelectedCellChange,
    renderRow,
    rowHasPendingChanges,
    sortColumns,
    setSortColumns,
    setSelectedRowIds,
    isFillDragging,
    setIsFillDragging,
    scheduleFillReset,
    isDesktop,
  } = gridProps;

  return (
    <>
      {contextHolder}

      <div id="harvest-entry" onKeyDown={onKeyDown}>
        <Sidebar
          active="spinach_harvest_entry"
          sidebarType="SpinachGrower"
          open={sidebarOpen}
          setOpen={setSidebarOpen}
        />

        <div
          className={`flex w-full flex-col pb-14 pt-8 lg:pt-6 sm:pt-4 ${
            sidebarOpen ? "pl-[210px] lg:pl-[50px]" : "pl-[50px]"
          } transition-[padding] duration-200 ease-in-out`}
          style={{ minHeight: 0 }}
        >
          <div className="mx-auto w-full max-w-[1900px] px-8 xl:px-6 lg:px-3 md:px-2 sm:px-1.5">
            <Dropdowns {...dropdownProps} />

            <div
              ref={gridWrapRef}
              className={`bg-white rounded-md shadow-sm ${
                isPseudoFullscreen ? "harvest-pseudo-fullscreen" : ""
              } ${isFullscreen ? "harvest-grid-wrap--fullscreen" : ""}`}
              style={{
                border: `1px solid ${borderGray}`,
                overflow: "hidden",
                background: "#fff",
                borderRadius: isFullscreen ? 0 : 10,
                height: isFullscreen
                  ? "100dvh"
                  : gridHeight
                    ? `${gridHeight}px`
                    : GRID_MIN_HEIGHT,
                minHeight: isFullscreen ? "100dvh" : GRID_MIN_HEIGHT,
                width: "100%",
                position: isPseudoFullscreen ? "fixed" : "relative",
                inset: isPseudoFullscreen ? 0 : "auto",
                zIndex: isFullscreen ? FULLSCREEN_Z : "auto",
              }}
            >
              <div
                className="harvest-grid"
                style={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  "--rdg-header-background-color": "#f5f5f5",
                  "--harvest-primary": selectionColor,
                  "--harvest-selection-fill": selectionFill,
                  "--rdg-selection-color": selectionColor,
                }}
              >
                <div
                  className="harvest-header-strip"
                  style={{
                    background: "var(--rdg-header-background-color)",
                    borderBottom: `1px solid ${borderGray}`,
                    padding: "6px 10px",
                    flex: "0 0 auto",
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onPointerDown={(e) => e.preventDefault()}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 sm:flex-col sm:items-stretch">
                    <div className="flex flex-wrap items-center gap-2 sm:order-2 sm:w-full">
                      <Button
                        size="medium"
                        icon={<UndoOutlined />}
                        onClick={undo}
                        disabled={!undoStack.length}
                      >
                        Undo
                      </Button>

                      <Button
                        size="medium"
                        icon={<RedoOutlined />}
                        onClick={redo}
                        disabled={!redoStack.length}
                      >
                        Redo
                      </Button>

                      <Dropdown
                        trigger={["click"]}
                        open={bulkActionsOpen}
                        onOpenChange={(o) => setBulkActionsOpen(o)}
                        getPopupContainer={getGridPopupContainer}
                        styles={{ root: { zIndex: DROPDOWN_Z } }}
                        disabled={
                          !selectedRowIds.size || loading || dropdownLoading
                        }
                        menu={{
                          items: bulkMenuItems.map((i) => ({
                            key: i.key,
                            label: i.label,
                            disabled: i.disabled,
                            onClick: () => {
                              setBulkActionsOpen(false);
                              i.onClick?.();
                            },
                          })),
                        }}
                      >
                        <Button
                          size="middle"
                          icon={<MoreOutlined />}
                          iconPlacement="end"
                          disabled={
                            !selectedRowIds.size || loading || dropdownLoading
                          }
                          className="whitespace-nowrap"
                        >
                          Bulk Actions
                        </Button>
                      </Dropdown>

                      <Button
                        size="middle"
                        type="primary"
                        onClick={handleSaveVisible}
                        loading={savingAll}
                        disabled={
                          !hasAnyPendingEdits || loading || dropdownLoading
                        }
                        className="whitespace-nowrap"
                      >
                        Save all changes
                      </Button>
                    </div>

                    <div className="flex justify-end sm:order-1 sm:w-full">
                      <Tooltip
                        title={
                          isFullscreen ? "Exit full screen" : "Full screen"
                        }
                      >
                        <button
                          type="button"
                          className="harvest-header-icon-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFullscreen();
                          }}
                          aria-label="Toggle fullscreen"
                        >
                          {isFullscreen ? (
                            <FullscreenExitOutlined />
                          ) : (
                            <FullscreenOutlined />
                          )}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {(loading || dropdownLoading) && (
                  <div className="harvest-loading-overlay">
                    <LoadingIcon height="90px" />
                  </div>
                )}

                <div
                  style={{ flex: "1 1 auto", minHeight: 0 }}
                  onPointerDown={(e) => {
                    const target = e.target;
                    if (
                      target instanceof Element &&
                      target.closest(".rdg-cell-drag-handle")
                    ) {
                      if (!isDesktop) return;
                      setIsFillDragging(true);
                    }
                  }}
                  onPointerUpCapture={() => {
                    if (isFillDragging) {
                      setIsFillDragging(false);
                      scheduleFillReset();
                    }
                  }}
                  onDoubleClickCapture={(e) => {
                    const target = e.target;
                    if (
                      target instanceof Element &&
                      target.closest(".rdg-cell-drag-handle")
                    ) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <DataGrid
                    ref={gridRef}
                    className={`rdg-light harvest-rdg${
                      isFillDragging ? " harvest-rdg--dragging-fill" : ""
                    }`}
                    columns={gridColumns}
                    rows={visibleRows}
                    onRowsChange={onRowsChange}
                    onFill={isDesktop ? onFill : undefined}
                    rowKeyGetter={(r) => r.id}
                    rowHeight={52}
                    headerRowHeight={72}
                    defaultColumnOptions={{ resizable: true }}
                    onCellClick={onCellClick}
                    onSelectedCellChange={onSelectedCellChange}
                    renderers={{ renderRow }}
                    rowClass={(row) =>
                      rowHasPendingChanges(row) ? "harvest-row--pending" : ""
                    }
                    sortColumns={sortColumns}
                    onSortColumnsChange={setSortColumns}
                    selectedRows={selectedRowIds}
                    onSelectedRowsChange={setSelectedRowIds}
                    style={{
                      height: "100%",
                      width: "100%",
                      pointerEvents:
                        loading || dropdownLoading ? "none" : "auto",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </>
  );
}
